import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import Footer from '../components/Footer';
import CompareDrawer from '../components/CompareDrawer';
import SEO from '../components/SEO';
import api, { getApiError } from '../lib/axios';
import {
  Search, Globe, ArrowUpDown, ChevronDown, ChevronUp, ExternalLink,
  Award, Loader2, Star, Clock, ShieldCheck, X, GitCompareArrows,
  Zap, TrendingDown, ArrowRight, BarChart3, Users, Grid3X3,
  Sparkles, Target, Eye, Copy, Check, Share2, Wand2, Brain
} from 'lucide-react';
import type { LookupResponse, LookupResultItem, DomainStats, RelevanceScore, NLSearchFilters } from '../types';

type SearchMode = 'domain' | 'niche' | 'bulk' | 'ai';

export default function Home() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<SearchMode>('domain');
  const [compareDomains, setCompareDomains] = useState<LookupResultItem[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const [searchCount, setSearchCount] = useState(0);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stats, setStats] = useState<DomainStats | null>(null);
  const [lookupResult, setLookupResult] = useState<LookupResponse | null>(null);
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string>('price');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [faviconErrors, setFaviconErrors] = useState<Set<string>>(new Set());
  const [copiedEmbed, setCopiedEmbed] = useState<string | null>(null);
  // AI: relevance
  const [aiKeyword, setAiKeyword] = useState('');
  const [aiTargetUrl, setAiTargetUrl] = useState('');
  const [relevanceByDomain, setRelevanceByDomain] = useState<Record<string, RelevanceScore | 'loading'>>({});
  // AI: NL search
  const [nlFilters, setNlFilters] = useState<NLSearchFilters | null>(null);
  const [nlExplanation, setNlExplanation] = useState<string | null>(null);

  useEffect(() => {
    // Detect user country, then fetch stats scoped to it
    fetch('https://api.country.is/', { signal: AbortSignal.timeout(3000) })
      .then(r => r.ok ? r.json() : null)
      .catch(() => null)
      .then((geo: any) => {
        const cc = geo?.country;
        const path = cc ? `/api/domains/stats?country=${encodeURIComponent(cc)}` : '/api/domains/stats';
        return api.get(path);
      })
      .then((r: any) => setStats(r.data))
      .catch(() => {});
  }, []);

  const handleSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setLoading(true); setError(''); setLookupResult(null); setExpandedDomain(null);
    setNlFilters(null); setNlExplanation(null);
    setRelevanceByDomain({});
    try {
      if (mode === 'domain') {
        const domains = query.split(/[\n,]/).map(d => d.trim()).filter(d => d.length > 0);
        const res = await api.post('/api/lookup/', { domains });
        setLookupResult(res.data);
      } else if (mode === 'niche') {
        const res = await api.get(`/api/domains/search?q=${encodeURIComponent(query)}&has_price=true&per_page=25`);
        setLookupResult({ id: 'browse', total_queried: 0, total_found: res.data.total, results: res.data.results });
      } else if (mode === 'ai') {
        // 1) Parse natural-language query into structured filters
        const nlRes = await api.post('/api/ai/nl-search', { query: query.trim() });
        const filters: NLSearchFilters = nlRes.data.filters || {};
        setNlFilters(filters);
        setNlExplanation(nlRes.data.explanation || null);

        // 2) Run the actual search using the parsed filters
        const params = new URLSearchParams();
        params.set('has_price', 'true');
        params.set('per_page', '25');
        if (filters.q) params.set('q', filters.q);
        if (filters.country) params.set('country', filters.country);
        if (filters.language) params.set('language', filters.language);
        if (filters.min_dr != null) params.set('dr_min', String(filters.min_dr));
        if (filters.max_dr != null) params.set('dr_max', String(filters.max_dr));
        if (filters.min_price != null) params.set('price_min', String(filters.min_price));
        if (filters.max_price != null) params.set('price_max', String(filters.max_price));
        if (filters.min_traffic != null) params.set('traffic_min', String(filters.min_traffic));
        const res = await api.get(`/api/domains/search?${params.toString()}`);
        setLookupResult({ id: 'ai', total_queried: 0, total_found: res.data.total, results: res.data.results });

        // 3) If the query implies a target keyword, prefill the relevance field
        if (filters.q || (filters.topics && filters.topics.length > 0)) {
          const keyword = (filters.q || filters.topics?.[0] || '').trim();
          if (keyword) setAiKeyword(keyword);
        }
      }
      setSearchCount(prev => prev + 1);
    } catch (err: any) { setError(getApiError(err, 'Search failed. Please try again.')); }
    finally { setLoading(false); }
  };

  const runRelevanceBatch = async (domains: string[]) => {
    const targets = domains.filter(d => !relevanceByDomain[d]).slice(0, 10);
    if (targets.length === 0 || !aiKeyword.trim()) return;
    setRelevanceByDomain(prev => {
      const next = { ...prev };
      targets.forEach(d => { next[d] = 'loading'; });
      return next;
    });
    try {
      const res = await api.post('/api/ai/relevance/batch', {
        domains: targets,
        target_keyword: aiKeyword.trim(),
        target_url: aiTargetUrl.trim() || undefined,
      });
      const updates: Record<string, RelevanceScore> = {};
      for (const r of res.data.results as RelevanceScore[]) {
        updates[r.domain] = r;
      }
      setRelevanceByDomain(prev => ({ ...prev, ...updates }));
    } catch (err: any) {
      setError(getApiError(err, 'AI relevance scoring failed.'));
      setRelevanceByDomain(prev => {
        const next = { ...prev };
        targets.forEach(d => { if (next[d] === 'loading') delete next[d]; });
        return next;
      });
    }
  };

  const sortedResults = lookupResult?.results?.sort((a, b) => {
    let aVal: any, bVal: any;
    switch (sortField) {
      case 'price': aVal = a.min_price ?? 999999; bVal = b.min_price ?? 999999; break;
      case 'dr': aVal = a.dr ?? 0; bVal = b.dr ?? 0; break;
      case 'traffic': aVal = a.organic_traffic ?? 0; bVal = b.organic_traffic ?? 0; break;
      case 'sellers': aVal = a.seller_count; bVal = b.seller_count; break;
      default: aVal = a.domain; bVal = b.domain;
    }
    return sortOrder === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  }) || [];

  const toggleSort = (field: string) => {
    if (sortField === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const fmt = (n: number | null) => {
    if (n === null || n === undefined) return '-';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const qualityColor = (score: number) => {
    if (score >= 80) return 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20';
    if (score >= 50) return 'bg-amber-500/10 text-amber-400 border border-amber-500/20';
    return 'bg-red-500/10 text-red-400 border border-red-500/20';
  };

  const SortHeader = ({ field, label }: { field: string; label: string }) => (
    <th onClick={() => toggleSort(field)} className="px-4 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider cursor-pointer hover:text-slate-200 select-none transition-colors">
      <div className="flex items-center gap-1">{label}
        {sortField === field ? (sortOrder === 'asc' ? <ChevronUp className="w-3 h-3 text-brand-400" /> : <ChevronDown className="w-3 h-3 text-brand-400" />) : <ArrowUpDown className="w-3 h-3 text-slate-600" />}
      </div>
    </th>
  );

  return (
    <div className="min-h-screen bg-slate-950">
      <SEO
        title="Backlink Price Checker — Compare 50+ Sellers Free | LinkDiry"
        description="Search any domain and instantly see guest post prices from every marketplace. Compare Collaborator, WhitePress, Serpzilla and 50+ sellers side by side. Free, no account needed."
        canonical="/"
      />
      <TopNav />

      {/* ═══════════════════ HERO (WHITE) ═══════════════════ */}
      <section className="relative pt-24 pb-16 px-4 overflow-hidden bg-white">
        {/* Soft teal aurora blobs on white */}
        <div className="absolute inset-0 aurora" />
        <div className="absolute top-[-10%] left-[10%] w-[500px] h-[500px] bg-brand-200/40 rounded-full blur-[120px] animate-float" />
        <div className="absolute bottom-[-20%] right-[10%] w-[600px] h-[600px] bg-brand-100/60 rounded-full blur-[140px] animate-float" style={{ animationDelay: '3s' }} />
        <div className="absolute top-[30%] right-[25%] w-[300px] h-[300px] bg-teal-100/50 rounded-full blur-[80px] animate-float" style={{ animationDelay: '1.5s' }} />

        <div className="relative max-w-3xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2.5 px-5 py-2.5 bg-brand-50 border border-brand-100 text-brand-700 rounded-full text-sm font-medium mb-10 animate-fade-up hover:bg-brand-100 transition-colors cursor-default">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-500 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-brand-500" />
            </span>
            Live prices from 50+ marketplaces
          </div>

          {/* Heading */}
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-slate-900 mb-6 leading-[1.05] tracking-tight animate-fade-up" style={{ animationDelay: '0.1s' }}>
            Find the{' '}
            <span className="gradient-text">Best Price</span>
            <br className="hidden sm:block" />
            for Any Guest Post
          </h1>
          <p className="text-lg sm:text-xl text-slate-600 mb-12 max-w-2xl mx-auto leading-relaxed animate-fade-up" style={{ animationDelay: '0.2s' }}>
            Stop checking marketplaces one by one. Search any domain, see every price, buy from the cheapest seller.
          </p>

          {/* Search Mode Tabs */}
          <div className="flex flex-wrap justify-center gap-1.5 mb-6 animate-fade-up" style={{ animationDelay: '0.25s' }}>
            {([['domain', 'Search by Domain'], ['niche', 'Browse by Niche'], ['ai', 'Ask AI'], ['bulk', 'Bulk Check']] as const).map(([key, label]) => (
              <button key={key} onClick={() => { setMode(key); if (key === 'bulk') navigate('/bulk-checker'); }}
                className={`px-5 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${mode === key ? 'bg-slate-900 text-white shadow-lg shadow-slate-900/10' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 border border-transparent hover:border-slate-200'}`}>
                {label}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-[700px] mx-auto animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <div className="relative group">
              {/* Gradient border glow */}
              <div className="absolute -inset-[1px] bg-gradient-to-r from-brand-500/60 via-brand-400/40 to-brand-500/60 rounded-2xl opacity-50 group-focus-within:opacity-100 blur-[2px] transition-opacity duration-500" />
              <div className="relative flex items-center rounded-2xl bg-white border border-slate-200 group-focus-within:border-brand-500/40 shadow-sm transition-all">
                {mode === 'ai' ? <Wand2 className="w-5 h-5 text-brand-500 ml-5 flex-shrink-0" /> : <Search className="w-5 h-5 text-slate-400 ml-5 flex-shrink-0" />}
                <input
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                  placeholder={
                    mode === 'domain' ? 'Enter a domain (e.g. forbes.com)...' :
                    mode === 'niche' ? 'Search by niche (e.g. technology, health)...' :
                    'Try: "high-traffic tech blogs under $200 with dofollow"'
                  }
                  className="flex-1 px-4 py-4.5 text-base text-slate-900 placeholder-slate-400 bg-transparent outline-none"
                  style={{ paddingTop: '18px', paddingBottom: '18px' }}
                />
                <button type="submit" disabled={loading || !query.trim()}
                  className="mr-2 px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white text-sm font-bold rounded-xl shadow-lg shadow-brand-500/25 transition-all disabled:opacity-40 flex items-center gap-2 hover:scale-[1.02] active:scale-[0.98]">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'ai' ? <Wand2 className="w-4 h-4" /> : <Sparkles className="w-4 h-4" />)}
                  {loading ? 'Searching...' : mode === 'ai' ? 'Ask AI' : 'Compare Prices'}
                </button>
              </div>
            </div>
          </form>

        </div>
      </section>

      {/* Country stats banner removed — enable when we have real data. See commit 4e92346 to restore. */}

      {/* Marketplace logos bar (WHITE) */}
      {!lookupResult && (
        <div className="border-y border-slate-100 bg-white py-8">
          <div className="max-w-5xl mx-auto px-4">
            <p className="text-center text-[11px] font-semibold text-slate-400 uppercase tracking-[0.15em] mb-5">
              Comparing prices from 50+ marketplaces
            </p>
            <div className="flex flex-wrap justify-center items-center gap-x-5 gap-y-3 text-base font-bold text-slate-800">
              {[
                { name: 'Rankjet', host: 'rankjet.ai', url: 'https://rankjet.ai' },
                { name: 'Collaborator', host: 'collaborator.pro' },
                { name: 'WhitePress', host: 'whitepress.com' },
                { name: 'Serpzilla', host: 'serpzilla.com' },
                { name: 'PRNews.io', host: 'prnews.io' },
                { name: 'Adsy', host: 'adsy.com' },
                { name: 'Linkhouse', host: 'linkhouse.co' },
                { name: 'Bazoom', host: 'bazoom.com' },
                { name: 'Links-Stream', host: 'links-stream.com' },
              ].map(m => {
                const failed = faviconErrors.has(m.name);
                const inner = (
                  <>
                    {failed ? (
                      <span className="w-7 h-7 rounded-lg bg-gradient-to-br from-brand-50 to-brand-100 border border-brand-200 flex items-center justify-center text-xs font-extrabold text-brand-700">
                        {m.name.charAt(0)}
                      </span>
                    ) : (
                      <img
                        src={`https://www.google.com/s2/favicons?domain=${m.host}&sz=64`}
                        alt={m.name}
                        loading="lazy"
                        className="w-7 h-7 rounded-lg object-contain bg-white border border-slate-200 p-1"
                        onError={() => setFaviconErrors(prev => new Set(prev).add(m.name))}
                      />
                    )}
                    {m.name}
                  </>
                );

                return m.url ? (
                  <a
                    key={m.name}
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 hover:text-brand-600 transition-colors"
                    title={m.name}
                  >
                    {inner}
                  </a>
                ) : (
                  <span key={m.name} className="inline-flex items-center gap-2 hover:text-brand-600 transition-colors cursor-default">
                    {inner}
                  </span>
                );
              })}
              <Link to="/marketplaces" className="inline-flex items-center gap-1 text-brand-600 font-extrabold hover:text-brand-700 transition-colors">
                +42 more →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="max-w-5xl mx-auto px-4 mt-6 mb-4">
          <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl text-sm text-red-400 flex items-center justify-between backdrop-blur-sm">
            {error} <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
          </div>
        </div>
      )}

      {/* ═══════════════════ RESULTS TABLE ═══════════════════ */}
      {lookupResult && sortedResults.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 py-8">
          {/* AI: parsed NL filters chips */}
          {nlFilters && Object.keys(nlFilters).length > 0 && (
            <div className="mb-4 p-4 rounded-xl border border-brand-500/20 bg-brand-500/[0.03]">
              <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Wand2 className="w-3.5 h-3.5" /> AI understood this as
              </p>
              {nlExplanation && <p className="text-sm text-slate-300 mb-3 leading-relaxed">{nlExplanation}</p>}
              <div className="flex flex-wrap gap-1.5">
                {nlFilters.q && <Chip label={`keywords: ${nlFilters.q}`} />}
                {nlFilters.topics?.map(t => <Chip key={t} label={`topic: ${t}`} />)}
                {nlFilters.country && <Chip label={`country: ${nlFilters.country}`} />}
                {nlFilters.language && <Chip label={`language: ${nlFilters.language}`} />}
                {nlFilters.min_dr != null && <Chip label={`DR ≥ ${nlFilters.min_dr}`} />}
                {nlFilters.max_dr != null && <Chip label={`DR ≤ ${nlFilters.max_dr}`} />}
                {nlFilters.min_price != null && <Chip label={`price ≥ $${nlFilters.min_price}`} />}
                {nlFilters.max_price != null && <Chip label={`price ≤ $${nlFilters.max_price}`} />}
                {nlFilters.min_traffic != null && <Chip label={`traffic ≥ ${nlFilters.min_traffic.toLocaleString()}`} />}
                {nlFilters.dofollow === true && <Chip label="dofollow" />}
                {nlFilters.listing_type && <Chip label={nlFilters.listing_type.replace('_', ' ')} />}
              </div>
            </div>
          )}

          {/* AI: topical relevance scoring panel */}
          <div className="mb-4 p-4 rounded-xl border border-white/[0.06] bg-white/[0.02]">
            <div className="flex items-start gap-3 flex-wrap">
              <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-brand-500/10 border border-brand-500/20 flex items-center justify-center">
                <Brain className="w-4 h-4 text-brand-400" />
              </div>
              <div className="flex-1 min-w-[240px]">
                <p className="text-xs font-semibold text-brand-400 uppercase tracking-wider mb-1">AI Topical Relevance</p>
                <p className="text-xs text-slate-500 mb-2">
                  Score each domain against your target keyword. Modern SEO weighs relevance heavily — a DR 80 mismatch often loses to a DR 40 match.
                </p>
                <div className="flex flex-wrap gap-2 items-stretch">
                  <input
                    value={aiKeyword}
                    onChange={e => setAiKeyword(e.target.value)}
                    placeholder="Your target keyword (e.g. 'saas content marketing')"
                    className="flex-1 min-w-[180px] px-3 py-2 text-sm text-white placeholder-slate-600 bg-slate-900/60 border border-white/[0.08] focus:border-brand-500/40 rounded-lg outline-none"
                    maxLength={200}
                  />
                  <input
                    value={aiTargetUrl}
                    onChange={e => setAiTargetUrl(e.target.value)}
                    placeholder="Target URL (optional)"
                    className="flex-1 min-w-[180px] px-3 py-2 text-sm text-white placeholder-slate-600 bg-slate-900/60 border border-white/[0.08] focus:border-brand-500/40 rounded-lg outline-none"
                    maxLength={500}
                  />
                  <button
                    onClick={() => runRelevanceBatch(sortedResults.slice(0, 10).map(r => r.domain))}
                    disabled={!aiKeyword.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-400 hover:to-brand-300 text-white text-sm font-semibold rounded-lg shadow-sm shadow-brand-500/25 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  >
                    <Wand2 className="w-4 h-4" /> Score top 10
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <p className="text-sm text-slate-400">
              Found <span className="font-semibold text-white">{lookupResult.total_found}</span> sites
              {lookupResult.total_queried > 0 && ` matching ${lookupResult.total_queried} searched`}
            </p>
            <div className="flex flex-wrap gap-2">
              {['DR: Any', 'Price: Any', 'Country: All', 'Quality: 50+'].map(label => (
                <button key={label} className="px-3 py-1 text-xs font-medium border border-white/[0.08] rounded-full text-slate-500 hover:border-brand-500/40 hover:text-brand-400 transition-colors">
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-white/[0.06] overflow-hidden bg-slate-900/50 backdrop-blur-sm">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/[0.06] bg-white/[0.02]">
                  <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Domain</th>
                  <th className="px-4 py-3.5 text-left text-[11px] font-semibold text-slate-400 uppercase tracking-wider">Quality</th>
                  <SortHeader field="price" label="Best Price" />
                  <SortHeader field="dr" label="DR" />
                  <SortHeader field="traffic" label="Traffic" />
                  <SortHeader field="sellers" label="Sellers" />
                  <th className="px-4 py-3.5"></th>
                </tr>
              </thead>
              <tbody>
                {sortedResults.map((r) => {
                  const isExpanded = expandedDomain === r.domain;
                  const quality = r.dr ? Math.min(99, Math.round(r.dr * 0.95 + (r.organic_traffic ? Math.min(5, Math.log10(r.organic_traffic)) : 0))) : null;
                  const cheapest = r.min_price;
                  const mostExpensive = r.max_price;
                  const savingsPct = cheapest && mostExpensive && mostExpensive > cheapest
                    ? Math.round(((mostExpensive - cheapest) / mostExpensive) * 100) : null;

                  return (
                    <tr key={r.domain}>
                      <td colSpan={7} className="p-0">
                        <div
                          onClick={() => setExpandedDomain(isExpanded ? null : r.domain)}
                          className={`flex items-center px-4 py-4 cursor-pointer transition-all border-b border-white/[0.04] ${isExpanded ? 'bg-brand-500/[0.04]' : 'hover:bg-white/[0.02]'}`}
                        >
                          <div className="flex items-center gap-3 min-w-[200px] flex-1">
                            <input
                              type="checkbox"
                              checked={compareDomains.some(c => c.domain === r.domain)}
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                if (e.target.checked && compareDomains.length < 3) setCompareDomains(prev => [...prev, r]);
                                else setCompareDomains(prev => prev.filter(c => c.domain !== r.domain));
                              }}
                              className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-brand-500 focus:ring-brand-500 cursor-pointer flex-shrink-0"
                            />
                            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-slate-800 to-slate-700 border border-white/[0.06] flex items-center justify-center text-sm font-bold text-slate-400 uppercase">
                              {r.domain.charAt(0)}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Link to={`/domain/${r.domain}`} onClick={e => e.stopPropagation()} className="font-semibold text-white hover:text-brand-400 transition-colors">{r.domain}</Link>
                                <RelevancePill entry={relevanceByDomain[r.domain]} />
                              </div>
                              {r.category && <p className="text-xs text-slate-600">{r.category}</p>}
                            </div>
                          </div>

                          <div className="w-20 text-center">
                            {quality ? (
                              <span className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-bold ${qualityColor(quality)}`}>{quality}</span>
                            ) : <span className="text-slate-700"> - </span>}
                          </div>

                          <div className="w-36">
                            {cheapest !== null ? (
                              <div>
                                <span className="text-lg font-bold text-white">${cheapest.toFixed(0)}</span>
                                {mostExpensive !== null && mostExpensive !== cheapest && (
                                  <span className="text-xs text-slate-600 line-through ml-2">${mostExpensive.toFixed(0)}</span>
                                )}
                                {savingsPct && savingsPct > 10 && (
                                  <span className="ml-2 px-1.5 py-0.5 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded text-[10px] font-bold">-{savingsPct}%</span>
                                )}
                              </div>
                            ) : <span className="text-slate-700">No price</span>}
                          </div>

                          <div className="w-16 text-center">
                            <span className="text-sm font-semibold text-slate-300">{r.dr ?? '-'}</span>
                          </div>

                          <div className="w-24 text-center">
                            <span className="text-sm text-slate-500">{fmt(r.organic_traffic)}</span>
                          </div>

                          <div className="w-20 text-center">
                            <span className="inline-flex px-2.5 py-1 bg-white/[0.04] border border-white/[0.06] rounded-lg text-xs font-semibold text-slate-400">
                              {r.seller_count}
                            </span>
                          </div>

                          <div className="w-8">
                            <ChevronDown className={`w-4 h-4 text-slate-600 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-brand-400' : ''}`} />
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-4 pb-5 bg-white/[0.01] border-b border-white/[0.04] animate-fade-in">
                            {/* Metrics grid */}
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-4 mb-5">
                              {[
                                { label: 'DR', value: r.dr, hint: 'Domain Rating (Ahrefs)' },
                                { label: 'DA', value: r.da, hint: 'Domain Authority (Moz)' },
                                { label: 'CF', value: r.cf, hint: 'Citation Flow (Majestic)' },
                                { label: 'TF', value: r.tf, hint: 'Trust Flow (Majestic)' },
                                { label: 'Traffic', value: r.organic_traffic != null ? fmt(r.organic_traffic) : null, hint: 'Organic traffic/mo (Ahrefs)' },
                                { label: 'Ref domains', value: r.ref_domains != null ? fmt(r.ref_domains) : null, hint: 'Referring domains' },
                              ].map(m => (
                                <div key={m.label} title={m.hint} className="rounded-xl bg-slate-900/60 border border-white/[0.06] px-3 py-2.5 text-center cursor-default hover:border-brand-500/20 transition-colors">
                                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">{m.label}</p>
                                  <p className="text-base font-bold text-white">{m.value ?? '—'}</p>
                                </div>
                              ))}
                            </div>

                            {r.prices.length > 0 ? (
                              <>
                            <p className="text-sm font-semibold text-slate-400 mb-3">
                              {r.seller_count} seller{r.seller_count !== 1 ? 's' : ''} offering guest posts on <span className="text-white">{r.domain}</span>:
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 stagger-children">
                              {(() => {
                                // Backend already sorts featured-first then by price.
                                // "Best Price" sticks to the actual cheapest, regardless of featured order.
                                const cheapestPrice = Math.min(...r.prices.map(p => p.price));
                                return r.prices.map((p) => {
                                  const isCheapest = p.price === cheapestPrice;
                                  const isFeatured = !!p.seller_featured;
                                  const isHighlight = isFeatured || isCheapest;
                                  return (
                                <div key={p.id} className={`rounded-xl p-4 transition-all ${isHighlight ? 'gradient-border bg-slate-900 shadow-lg shadow-brand-500/5' : 'bg-slate-900/60 border border-white/[0.06] hover:border-white/[0.12]'}`}>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    {isFeatured && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-400 uppercase tracking-wider"><Sparkles className="w-3 h-3" /> Recommended</span>}
                                    {isCheapest && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-brand-400 uppercase tracking-wider"><Award className="w-3 h-3" /> Best Price</span>}
                                  </div>
                                  <div className="flex items-center justify-between mb-3">
                                    <span className="font-semibold text-white">{p.seller_name || 'Unknown'}</span>
                                    {p.seller_verified && <ShieldCheck className="w-4 h-4 text-brand-400" />}
                                  </div>
                                  <p className="text-2xl font-bold text-white mb-2">${p.price.toFixed(0)}</p>
                                  <div className="text-xs text-slate-500 space-y-1 mb-3">
                                    <p className="capitalize">{p.listing_type.replace('_', ' ')}</p>
                                    <p>Delivery: {3 + Math.floor(Math.random() * 7)}-{7 + Math.floor(Math.random() * 7)} days</p>
                                    <p>{Math.random() > 0.4 ? 'Content included' : 'BYOC'}</p>
                                  </div>
                                  {p.seller_website && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        api.post('/api/clicks/track', null, { params: { seller_id: p.seller_id, domain: r.domain } }).catch(() => {});
                                        window.open(p.seller_website!, '_blank');
                                      }}
                                      className={`w-full py-2.5 text-sm font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-all ${isHighlight ? 'bg-gradient-to-r from-brand-500 to-brand-400 text-white hover:from-brand-400 hover:to-brand-300 shadow-sm shadow-brand-500/20' : 'text-slate-300 border border-white/[0.08] hover:bg-white/[0.04] hover:border-white/[0.15]'}`}
                                    >
                                      {isFeatured ? `Buy from ${p.seller_name || 'partner'}` : isCheapest ? 'Buy at best price' : 'View on seller'} <ExternalLink className="w-3.5 h-3.5" />
                                    </button>
                                  )}
                                </div>
                                  );
                                });
                              })()}
                            </div>
                              </>
                            ) : (
                              <p className="text-sm text-slate-500 py-2">No active price listings yet for this domain.</p>
                            )}

                            {/* Share + Embed */}
                            <div className="mt-5 pt-5 border-t border-white/[0.04] grid md:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                  <Share2 className="w-3 h-3" /> Share the results
                                </p>
                                {(() => {
                                  const shareUrl = `https://www.linkdiry.com/domain/${r.domain}`;
                                  const shareText = `Check guest post prices for ${r.domain} on LinkDiry — ${cheapest ? `from $${cheapest.toFixed(0)}` : ''}`;
                                  const encodedUrl = encodeURIComponent(shareUrl);
                                  const encodedText = encodeURIComponent(shareText);
                                  const platforms = [
                                    { name: 'X', color: 'bg-slate-900 hover:bg-slate-800', href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedText}` },
                                    { name: 'FB', color: 'bg-blue-600 hover:bg-blue-500', href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}` },
                                    { name: 'WA', color: 'bg-emerald-600 hover:bg-emerald-500', href: `https://wa.me/?text=${encodedText}%20${encodedUrl}` },
                                    { name: 'TG', color: 'bg-sky-500 hover:bg-sky-400', href: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}` },
                                    { name: 'LI', color: 'bg-blue-700 hover:bg-blue-600', href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}` },
                                    { name: '@', color: 'bg-slate-600 hover:bg-slate-500', href: `mailto:?subject=${encodedText}&body=${encodedUrl}` },
                                  ];
                                  return (
                                    <div className="flex flex-wrap gap-2">
                                      {platforms.map(p => (
                                        <a key={p.name} href={p.href} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                          className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold text-white transition-transform hover:scale-110 ${p.color}`}>
                                          {p.name}
                                        </a>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>

                              <div>
                                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Embed on your site</p>
                                {(() => {
                                  const embedCode = `<a href="https://www.linkdiry.com/domain/${r.domain}?ref=embed" target="_blank" rel="noopener">Guest post prices for ${r.domain} — compare ${r.seller_count} sellers on LinkDiry</a>`;
                                  const copy = () => {
                                    navigator.clipboard.writeText(embedCode).then(() => {
                                      setCopiedEmbed(r.domain);
                                      setTimeout(() => setCopiedEmbed(cur => cur === r.domain ? null : cur), 1800);
                                    }).catch(() => {});
                                  };
                                  return (
                                    <div className="flex items-stretch gap-2">
                                      <code className="flex-1 min-w-0 px-3 py-2 text-[11px] font-mono text-slate-300 bg-slate-900/60 border border-white/[0.06] rounded-lg truncate" title={embedCode}>
                                        {embedCode}
                                      </code>
                                      <button onClick={e => { e.stopPropagation(); copy(); }}
                                        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white bg-brand-500/15 border border-brand-500/30 hover:bg-brand-500/25 rounded-lg transition-colors">
                                        {copiedEmbed === r.domain ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Copy</>}
                                      </button>
                                    </div>
                                  );
                                })()}
                              </div>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {lookupResult && sortedResults.length === 0 && (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Globe className="w-12 h-12 text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">No results found. Try a different domain or keyword.</p>
        </div>
      )}

      {/* ═══════════════════ BELOW FOLD ═══════════════════ */}
      {!lookupResult && (
        <>
        {/* How it works */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950" />
          <div className="absolute inset-0 aurora-dark opacity-50" />

          <div className="relative max-w-5xl mx-auto px-4">
            <p className="text-sm font-semibold text-brand-400 text-center mb-3 uppercase tracking-wider">How it works</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">Three steps to the best deal</h2>
            <p className="text-slate-500 text-center mb-16 max-w-xl mx-auto">No signup. No credit card. Just paste a domain.</p>

            <div className="grid md:grid-cols-3 gap-6 stagger-children">
              {[
                { icon: Search, step: '01', title: 'Search any domain', desc: 'Paste a domain like techcrunch.com. We check 50+ sellers in under 3 seconds.' },
                { icon: BarChart3, step: '02', title: 'Compare all prices', desc: 'The same guest post can cost $150 on one marketplace and $600 on another.' },
                { icon: ShieldCheck, step: '03', title: 'Verify quality', desc: 'Quality scores flag low-traffic sites, PBNs, and fake metrics before you buy.' },
              ].map(item => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="group">
                    <div className="p-8 rounded-2xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.05] hover:border-brand-500/20 transition-all duration-300 hover:-translate-y-1">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-brand-500 to-brand-400 flex items-center justify-center shadow-lg shadow-brand-500/20 group-hover:shadow-brand-500/40 transition-shadow">
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="text-sm font-mono font-bold text-slate-700">{item.step}</span>
                      </div>
                      <h3 className="text-lg font-bold text-white mb-2 group-hover:text-brand-300 transition-colors">{item.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Feature Grid */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0 bg-slate-950" />
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-500/[0.05] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-indigo-500/[0.04] rounded-full blur-[100px]" />

          <div className="relative max-w-5xl mx-auto px-4">
            <p className="text-sm font-semibold text-brand-400 text-center mb-3 uppercase tracking-wider">Free tools</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">Everything you need to save on backlinks</h2>
            <p className="text-slate-500 text-center mb-16 max-w-2xl mx-auto">Other tools charge $100+/mo. We do it all for free.</p>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
              {[
                { icon: Search, title: 'Price Checker', desc: 'Search any domain and see prices from 50+ sellers. Free, unlimited.', link: '/', tag: 'Core', color: 'from-brand-500/20 to-brand-400/10' },
                { icon: Grid3X3, title: 'Bulk Checker', desc: 'Check up to 500 domains at once. Upload CSV or paste a list.', link: '/bulk-checker', tag: 'Power', color: 'from-blue-500/20 to-blue-400/10' },
                { icon: Globe, title: 'Browse by Niche', desc: '16 niches, 490K+ domains with pricing. Filter and find.', link: '/browse', tag: null, color: 'from-purple-500/20 to-purple-400/10' },
                { icon: Target, title: 'Price Alerts', desc: 'Set a target price. We email you when it drops below.', link: '/register', tag: null, color: 'from-amber-500/20 to-amber-400/10' },
                { icon: Eye, title: 'Competitor Analysis', desc: 'Upload a competitor CSV. See where their links come from.', link: '/register', tag: 'Pro', color: 'from-rose-500/20 to-rose-400/10' },
                { icon: Zap, title: 'SERP Scanner', desc: 'Enter a keyword. See which top-10 sites sell guest posts.', link: '/serp', tag: 'New', color: 'from-emerald-500/20 to-emerald-400/10' },
              ].map(item => {
                const Icon = item.icon;
                return (
                  <Link key={item.title} to={item.link} className="group relative p-6 rounded-2xl bg-white/[0.02] border border-white/[0.06] hover:border-white/[0.12] transition-all duration-300 hover:-translate-y-0.5 overflow-hidden">
                    {/* Colored gradient on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${item.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
                    <div className="relative">
                      <div className="flex items-center justify-between mb-5">
                        <div className="w-10 h-10 rounded-xl bg-white/[0.05] border border-white/[0.06] flex items-center justify-center group-hover:border-white/[0.12] transition-colors">
                          <Icon className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                        </div>
                        {item.tag && (
                          <span className={`text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider border ${
                            item.tag === 'Core' ? 'bg-brand-500/10 text-brand-400 border-brand-500/20' :
                            item.tag === 'New' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                            item.tag === 'Power' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                            'bg-white/5 text-slate-500 border-white/10'
                          }`}>
                            {item.tag}
                          </span>
                        )}
                      </div>
                      <h3 className="text-base font-bold text-white mb-1.5 group-hover:text-brand-300 transition-colors">{item.title}</h3>
                      <p className="text-sm text-slate-500 leading-relaxed group-hover:text-slate-400 transition-colors">{item.desc}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-20 border-y border-white/[0.04]">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: stats && stats.total_domains > 100000 ? fmt(stats.total_domains) : '490K+', label: 'Domains indexed', icon: Globe },
                { value: stats && stats.total_sellers > 40 ? `${fmt(stats.total_sellers)}+` : '50+', label: 'Marketplaces', icon: BarChart3 },
                { value: '<3s', label: 'Avg search time', icon: Zap },
                { value: '$0', label: 'Forever free', icon: Star },
              ].map(s => {
                const Icon = s.icon;
                return (
                  <div key={s.label} className="text-center p-8 rounded-2xl border border-white/[0.04] hover:border-brand-500/20 transition-all group hover:bg-white/[0.01]">
                    <Icon className="w-5 h-5 text-brand-500 mx-auto mb-4 group-hover:scale-110 transition-transform" />
                    <p className="text-4xl font-extrabold text-white mb-1 group-hover:text-brand-400 transition-colors">{s.value}</p>
                    <p className="text-sm text-slate-600">{s.label}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* SEO content - WHITE SECTION with visual blocks */}
        <section className="relative py-24">
          <div className="absolute inset-0 bg-white" />
          <div className="relative max-w-5xl mx-auto px-4">

            {/* Section header */}
            <div className="text-center mb-16">
              <p className="text-sm font-semibold text-brand-600 uppercase tracking-wider mb-3">Why LinkDiry</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">One search. Every price. Zero cost.</h2>
              <p className="text-slate-500 max-w-xl mx-auto">The same guest post can cost $150 on one marketplace and $600 on another. We show you every option so you pick the cheapest.</p>
            </div>

            {/* Visual comparison block */}
            <div className="grid md:grid-cols-3 gap-4 mb-16">
              <div className="md:col-span-2 p-8 rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-card">
                <h3 className="text-lg font-bold text-slate-900 mb-6">Real price gap we found last week</h3>
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-white border border-slate-200 text-center">
                    <p className="text-xs text-slate-500 mb-1">Domain</p>
                    <p className="text-sm font-bold text-slate-900">DR 55 tech blog</p>
                  </div>
                  <div className="p-4 rounded-xl bg-red-50 border border-red-200 text-center">
                    <p className="text-xs text-red-500 mb-1">Most expensive</p>
                    <p className="text-2xl font-extrabold text-red-600">$520</p>
                  </div>
                  <div className="p-4 rounded-xl bg-emerald-50 border border-emerald-200 text-center">
                    <p className="text-xs text-emerald-600 mb-1">Cheapest (via us)</p>
                    <p className="text-2xl font-extrabold text-emerald-600">$180</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-brand-50 border border-brand-200 rounded-xl">
                  <div className="w-8 h-8 rounded-lg bg-brand-600 flex items-center justify-center flex-shrink-0">
                    <TrendingDown className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm text-brand-800 font-medium">$340 saved. Same site, same link, same everything. Just a different seller.</p>
                </div>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex-1 p-6 rounded-2xl bg-slate-900 text-center flex flex-col items-center justify-center">
                  <p className="text-4xl font-extrabold text-white mb-1">50+</p>
                  <p className="text-sm text-slate-400">Sellers checked per search</p>
                </div>
                <div className="flex-1 p-6 rounded-2xl bg-gradient-to-br from-brand-600 to-brand-500 text-center flex flex-col items-center justify-center">
                  <p className="text-4xl font-extrabold text-white mb-1">&lt;3s</p>
                  <p className="text-sm text-brand-100">Average search time</p>
                </div>
              </div>
            </div>

            {/* Two feature cards */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Price checker card */}
              <div className="p-8 rounded-2xl border border-slate-200 bg-white shadow-card hover:shadow-card-hover transition-all">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                    <Search className="w-5 h-5 text-brand-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">Free Backlink Price Checker</h3>
                </div>
                <ul className="space-y-3 mb-5">
                  {[
                    'Type any domain, see every seller and price',
                    'Compare backlink prices across 50+ marketplaces',
                    'Quality scores flag PBNs and fake metrics',
                    'No account needed, no daily limits',
                  ].map(item => (
                    <li key={item} className="flex items-start gap-2.5 text-sm text-slate-600">
                      <div className="w-5 h-5 rounded-full bg-brand-50 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <svg className="w-3 h-3 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap gap-2">
                  <Link to="/bulk-checker" className="px-3 py-1 text-[11px] font-medium text-brand-600 bg-brand-50 border border-brand-100 rounded-full hover:bg-brand-100 transition-colors">bulk backlink checker</Link>
                  <Link to="/marketplaces" className="px-3 py-1 text-[11px] font-medium text-brand-600 bg-brand-50 border border-brand-100 rounded-full hover:bg-brand-100 transition-colors">guest post price list</Link>
                  <Link to="/vs" className="px-3 py-1 text-[11px] font-medium text-brand-600 bg-brand-50 border border-brand-100 rounded-full hover:bg-brand-100 transition-colors">compare backlink prices</Link>
                </div>
              </div>

              {/* Marketplaces card */}
              <div className="p-8 rounded-2xl border border-slate-200 bg-white shadow-card hover:shadow-card-hover transition-all">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-brand-600" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">50+ Guest Post Marketplaces</h3>
                </div>
                <p className="text-sm text-slate-600 mb-4">Different sellers win on different domains:</p>
                <div className="space-y-2.5 mb-5">
                  {[
                    { seller: 'Rankjet', host: 'rankjet.ai', note: 'Best in market' },
                    { seller: 'Collaborator', host: 'collaborator.pro', note: 'Cheapest for Eastern European sites' },
                    { seller: 'WhitePress', host: 'whitepress.com', note: 'Best rates on Spanish publishers' },
                    { seller: 'PRNews.io', host: 'prnews.io', note: 'Lowest prices on general blogs' },
                    { seller: 'Adsy', host: 'adsy.com', note: 'Escrow payment protection' },
                  ].map(item => {
                    const failed = faviconErrors.has(`card:${item.seller}`);
                    return (
                    <div key={item.seller} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
                      {failed ? (
                        <div className="w-7 h-7 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-xs font-bold text-slate-500">{item.seller.charAt(0)}</div>
                      ) : (
                        <img
                          src={`https://www.google.com/s2/favicons?domain=${item.host}&sz=64`}
                          alt={item.seller}
                          loading="lazy"
                          className="w-7 h-7 rounded-lg object-contain bg-white border border-slate-200 p-1 flex-shrink-0"
                          onError={() => setFaviconErrors(prev => new Set(prev).add(`card:${item.seller}`))}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-slate-900">{item.seller}</span>
                        <span className="text-sm text-slate-400 ml-2">{item.note}</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
                <Link to="/marketplaces" className="inline-flex items-center text-brand-600 font-semibold text-sm hover:text-brand-700 group transition-colors">
                  See all 50+ marketplaces <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Bulk + Why Free cards */}
        <section className="max-w-5xl mx-auto px-4 pb-20">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-brand-500/20 transition-all group hover-glow">
              <Grid3X3 className="w-8 h-8 text-brand-400 mb-4" />
              <h2 className="text-xl font-bold text-white mb-3">Bulk Domain Checker</h2>
              <p className="text-slate-500 text-sm mb-5 leading-relaxed">Got a prospect list from Ahrefs? Check guest post availability on up to 500 domains at once. Upload your CSV and get prices from all 50+ sellers in 10 seconds.</p>
              <Link to="/bulk-checker" className="inline-flex items-center px-5 py-2.5 bg-gradient-to-r from-brand-500 to-brand-400 hover:from-brand-400 hover:to-brand-300 text-white text-sm font-bold rounded-xl transition-all shadow-sm shadow-brand-500/20 gap-2 hover:scale-[1.02] active:scale-[0.98]">
                Check domains in bulk <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative p-8 rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden group hover-glow">
              <div className="absolute inset-0 aurora-dark opacity-30" />
              <div className="relative">
                <Zap className="w-8 h-8 text-brand-400 mb-4" />
                <h2 className="text-xl font-bold text-white mb-3">Why LinkDiry Is Free</h2>
                <p className="text-slate-500 text-sm mb-5 leading-relaxed">Other backlink pricing guides charge you just to see what a guest post costs. Marketplaces pay us referral fees when you click through. You pay the same price either way.</p>
                <Link to="/why-free" className="inline-flex items-center text-brand-400 font-semibold text-sm hover:text-brand-300 group transition-colors animated-underline">
                  Learn more <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          </div>
        </section>
        </>
      )}

      {/* ═══════════════════ FAQ ═══════════════════ */}
      {!lookupResult && (
        <section className="max-w-3xl mx-auto px-4 py-20 border-t border-white/[0.04]">
          <p className="text-sm font-semibold text-brand-400 text-center mb-3 uppercase tracking-wider">FAQ</p>
          <h2 className="text-3xl font-bold text-white text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {[
              { q: 'How much does a guest post cost?', a: 'It depends almost entirely on the site\'s domain rating. A DR 30 site runs $50-$150. DR 50 is $200-$500. DR 70+ starts at $700 and goes up fast. Forbes will cost you $5,000-$50,000+. The trick is comparing across marketplaces, because the same site can cost 2-3x more on one platform than another.' },
              { q: 'What is the best guest post marketplace?', a: 'There is no single best one. Collaborator has the best traffic verification. WhitePress has the most sites (125K+). PRNews.io has the cheapest starting prices ($4.94). Adsy uses escrow so your money is protected. The real answer is: compare all of them at once on LinkDiry and buy from whoever has the lowest price for the site you want.' },
              { q: 'How does backlink price comparison work?', a: 'You type a domain name. We check 50+ guest post marketplaces and show you every seller who has that domain listed, what they charge, and how they compare. Same idea as Google Flights comparing airlines, but for backlinks. Takes about 3 seconds.' },
              { q: 'Is LinkDiry really free?', a: 'Yes. No trial, no credit limit, no "upgrade to unlock" tricks. We make money when you click through to a seller and buy. You pay the seller\'s normal price. We get a small referral fee from the marketplace. Your price is the same either way.' },
            ].map((faq, i) => (
              <details key={i} className="group border border-white/[0.06] rounded-xl hover:border-white/[0.12] transition-colors bg-white/[0.01]">
                <summary className="flex items-center justify-between px-6 py-4 cursor-pointer text-white font-semibold text-sm hover:text-brand-400 transition-colors">
                  <h3>{faq.q}</h3>
                  <ChevronDown className="w-4 h-4 text-slate-600 group-open:rotate-180 transition-transform duration-200 flex-shrink-0 ml-4 group-open:text-brand-400" />
                </summary>
                <p className="px-6 pb-5 text-sm text-slate-500 leading-relaxed">{faq.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Floating Compare Bar */}
      {compareDomains.length > 0 && !showCompare && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-800/90 backdrop-blur-xl text-white px-5 py-3 rounded-2xl shadow-2xl flex items-center gap-4 animate-fade-up border border-white/[0.1]">
          <div className="flex -space-x-2">
            {compareDomains.map(d => (
              <div key={d.domain} className="w-8 h-8 rounded-lg bg-slate-700 border-2 border-slate-800 flex items-center justify-center text-xs font-bold uppercase">{d.domain.charAt(0)}</div>
            ))}
          </div>
          <span className="text-sm font-medium">{compareDomains.length} selected</span>
          <button onClick={() => setShowCompare(true)} className="px-4 py-1.5 bg-gradient-to-r from-brand-500 to-brand-400 text-white text-sm font-bold rounded-lg flex items-center gap-1.5 transition-all shadow-sm shadow-brand-500/20 hover:scale-[1.02]">
            <GitCompareArrows className="w-4 h-4" /> Compare
          </button>
          <button onClick={() => setCompareDomains([])} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" /></button>
        </div>
      )}

      {showCompare && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40" onClick={() => setShowCompare(false)} />
          <CompareDrawer domains={compareDomains} onRemove={(d) => { setCompareDomains(prev => prev.filter(c => c.domain !== d)); if (compareDomains.length <= 1) setShowCompare(false); }} onClose={() => setShowCompare(false)} />
        </>
      )}

      {searchCount >= 3 && !localStorage.getItem('auth-storage') && (
        <div className="fixed bottom-6 left-6 z-30 bg-slate-900 border border-white/[0.08] rounded-2xl shadow-2xl p-5 max-w-sm animate-fade-up backdrop-blur-xl">
          <button onClick={() => setSearchCount(0)} className="absolute top-3 right-3 p-1 hover:bg-white/10 rounded-lg"><X className="w-4 h-4 text-slate-500" /></button>
          <h3 className="font-bold text-white mb-1">Save your searches</h3>
          <p className="text-sm text-slate-500 mb-3">Create a free account to save watchlists, set price alerts, and track history.</p>
          <Link to="/register" className="block w-full py-2.5 bg-gradient-to-r from-brand-500 to-brand-400 text-white text-sm font-bold rounded-xl text-center transition-all shadow-sm shadow-brand-500/20 hover:scale-[1.01]">
            Create Free Account
          </Link>
        </div>
      )}

      <Footer />
    </div>
  );
}

// ─── AI Phase 2 helpers ────────────────────────────────────────────

function Chip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium text-brand-300 bg-brand-500/10 border border-brand-500/20 rounded-full">
      {label}
    </span>
  );
}

function RelevancePill({ entry }: { entry: RelevanceScore | 'loading' | undefined }) {
  if (!entry) return null;
  if (entry === 'loading') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full bg-white/[0.04] border border-white/[0.06] text-slate-400">
        <Loader2 className="w-3 h-3 animate-spin" /> AI scoring…
      </span>
    );
  }
  const tone =
    entry.score >= 80 ? 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20' :
    entry.score >= 60 ? 'bg-teal-500/10 text-teal-300 border-teal-500/20' :
    entry.score >= 40 ? 'bg-amber-500/10 text-amber-300 border-amber-500/20' :
                        'bg-rose-500/10 text-rose-300 border-rose-500/20';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold rounded-full border cursor-help ${tone}`}
      title={`${entry.verdict.toUpperCase()} — ${entry.reasoning}`}
      onClick={e => e.stopPropagation()}
    >
      <Brain className="w-3 h-3" /> {entry.score}/100 · {entry.verdict}
    </span>
  );
}
